'use client';

import { useEffect, useCallback, useState } from 'react';
import {
  LiveKitRoom,
  useVoiceAssistant,
  BarVisualizer,
  RoomAudioRenderer,
} from '@livekit/components-react';
import '@livekit/components-styles';
import { Room } from 'livekit-client';
import { Mic, MicOff, PhoneOff, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

export interface MomentoVoice {
  id: string;
  name: string;
  description: string;
  avatar: string;
}

interface VoiceAgentModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  voice: MomentoVoice;
}

export function VoiceAgentModal({ open, onOpenChange, voice }: VoiceAgentModalProps) {
  const [connectionDetails, setConnectionDetails] = useState<{
    serverUrl: string;
    participantToken: string;
  } | null>(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const connect = useCallback(async () => {
    setIsConnecting(true);
    setError(null);
    try {
      const res = await fetch('/api/voice-agent/token', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          voice_name: voice.name,
          voice_id: voice.id,
        }),
      });

      if (!res.ok) throw new Error('Failed to connect');

      const data = await res.json();
      setConnectionDetails({
        serverUrl: data.serverUrl,
        participantToken: data.participantToken,
      });
    } catch (err) {
      setError('Could not start conversation. Please try again.');
      console.error('[VoiceAgent] Connection error:', err);
    } finally {
      setIsConnecting(false);
    }
  }, [voice]);

  useEffect(() => {
    if (open && !connectionDetails && !isConnecting) {
      connect();
    }
    if (!open) {
      setConnectionDetails(null);
      setError(null);
    }
  }, [open]);

  const handleDisconnect = useCallback(() => {
    setConnectionDetails(null);
    onOpenChange(false);
  }, [onOpenChange]);

  return (
    <Dialog open={open} onOpenChange={(isOpen) => {
      if (!isOpen) {
        setConnectionDetails(null);
      }
      onOpenChange(isOpen);
    }}>
      <DialogContent className="sm:max-w-md p-0 gap-0 overflow-hidden bg-background/95 backdrop-blur-xl border-border/50 [&>button]:hidden">
        {isConnecting && (
          <div className="flex flex-col items-center justify-center py-16 gap-4">
            <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
            <p className="text-sm text-muted-foreground">Connecting to {voice.name}...</p>
          </div>
        )}

        {error && (
          <div className="flex flex-col items-center justify-center py-16 gap-4">
            <p className="text-sm text-destructive">{error}</p>
            <Button variant="outline" size="sm" onClick={connect}>Try Again</Button>
          </div>
        )}

        {connectionDetails && (
          <LiveKitRoom
            serverUrl={connectionDetails.serverUrl}
            token={connectionDetails.participantToken}
            connect={true}
            audio={true}
            video={false}
            onDisconnected={handleDisconnect}
            onError={(err) => {
              if (err.message?.includes('Permission') || err.message?.includes('NotAllowed')) {
                console.warn('[VoiceAgent] Media permission (non-fatal):', err.message);
                return;
              }
              console.error('[VoiceAgent] Room error:', err);
            }}
          >
            <AgentConversationView voice={voice} onEnd={handleDisconnect} />
            <RoomAudioRenderer />
          </LiveKitRoom>
        )}
      </DialogContent>
    </Dialog>
  );
}

function AgentConversationView({
  voice,
  onEnd,
}: {
  voice: MomentoVoice;
  onEnd: () => void;
}) {
  const { state, audioTrack } = useVoiceAssistant();

  const stateLabel = (() => {
    switch (state) {
      case 'connecting':
      case 'initializing':
        return 'Connecting...';
      case 'listening':
        return 'Listening...';
      case 'thinking':
        return 'Thinking...';
      case 'speaking':
        return 'Speaking...';
      case 'disconnected':
        return 'Disconnected';
      default:
        return 'Connecting...';
    }
  })();

  return (
    <div className="flex flex-col items-center py-8 px-6 gap-6">
      {/* Agent avatar with state ring */}
      <div className={cn(
        'relative w-28 h-28 rounded-full overflow-hidden ring-4 transition-all duration-500',
        state === 'speaking' ? 'ring-green-500/60 scale-105' :
        state === 'listening' ? 'ring-blue-500/40' :
        state === 'thinking' ? 'ring-yellow-500/40 animate-pulse' :
        'ring-border/30'
      )}>
        <img
          src={voice.avatar}
          alt={voice.name}
          className="w-full h-full object-cover"
        />
      </div>

      {/* Agent name + state */}
      <div className="text-center">
        <h3 className="text-lg font-semibold text-foreground">{voice.name}</h3>
        <p className="text-sm text-muted-foreground">{stateLabel}</p>
      </div>

      {/* Audio visualizer */}
      <div className="w-full h-16 flex items-center justify-center" data-lk-theme="default">
        {audioTrack ? (
          <BarVisualizer
            trackRef={audioTrack}
            state={state}
            barCount={5}
            options={{ minHeight: 8 }}
          />
        ) : (
          <div className="flex gap-1.5 items-end h-12">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="w-3 bg-muted-foreground/20 rounded-full transition-all"
                style={{ height: `${8 + (i % 3) * 4}px` }} />
            ))}
          </div>
        )}
      </div>

      {/* End button */}
      <div className="flex items-center gap-4">
        <Button
          variant="destructive"
          size="lg"
          className="rounded-full px-8 gap-2"
          onClick={onEnd}
        >
          <PhoneOff className="w-4 h-4" />
          End Conversation
        </Button>
      </div>

      {/* CTA */}
      <div className="text-center pt-4 border-t border-border/30 w-full">
        <p className="text-xs text-muted-foreground leading-relaxed">
          Create a free account to start recording moments
          <br />and invite your team to create together.
        </p>
        <Button variant="link" size="sm" className="text-xs mt-1" asChild>
          <a href="/login">Sign up free</a>
        </Button>
      </div>
    </div>
  );
}
